import { BoxWrapper, Button, Prompt } from 'components/Base';
import React, { useEffect, useState, useRef } from 'react';
import api from 'api';
import Fields from 'components/Fields';
import { Field, reduxForm, InjectedFormProps, change } from 'redux-form';
import { makeStyles } from '@material-ui/core';
import { Notify } from 'utils';
import { ContentCopy, Description } from '@mui/icons-material';
import FloatingModal from 'components/Base/Modal/FloatingModal/FloatingModal';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { getFormValues } from 'redux-form';
import { useUnsavedChangesWarning } from '../utils';
import Store from 'store/store';

const { selectors } = Store;

const useStyles = makeStyles({
  field: {
    width: '100%',
    minHeight: '50px',
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '0 0 25px 25px'
  }
});

type RiskDescriptionsProps = {
  isFirmAdmin: boolean;
  setDirty: Function;
};

type Description = {
  risk_level: string;
  risk_type: string;
  description: string;
  risk_description: string;
};

type Props = InjectedFormProps<Record<string, string>, RiskDescriptionsProps> &
  RiskDescriptionsProps & {
    formValues: Record<string, string>;
    riskGroup: any;
  };

const RiskDescriptionsBase: React.FC<Props> = ({
  isFirmAdmin,
  handleSubmit,
  initialize,
  dirty,
  setDirty,
  riskGroup
}) => {
  const [customDescriptions, setCustomDescriptions] = useState<Description[]>(
    []
  );
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [isLeaveModalVisible, setLeaveModalVisible] = useState(false);
  const [isResetModalVisible, setResetModalVisible] = useState(false);
  const [isCopyModalVisible, setCopyModalVisible] = useState(false);
  const [targetLocation, setTargetLocation] = useState(null) as any;
  const [clickedIconRef, setClickedIconRef] =
    useState<React.RefObject<HTMLSpanElement> | null>(null);
  const [clickedDescription, setClickedDescription] =
    useState<Description | null>(null);
  const classes = useStyles();
  const formValues: Record<string, string> =
    useSelector((state: any) => getFormValues('riskDescriptionsForm')(state)) ||
    {};
  const iconContainerRefs = useRef<(HTMLSpanElement | null)[]>([]);

  useUnsavedChangesWarning({
    dirty,
    confirmLeave,
    targetLocation,
    setLeaveModalVisible,
    setDirty,
    setTargetLocation
  });

  useEffect(() => {
    const fetchData = async () => {
      const defaultResponse = (await api.getFirmRiskDescriptions()) as {
        data: Description[];
      };

      setCustomDescriptions(defaultResponse.data);

      const initialFormValues = defaultResponse.data.reduce<
        Record<string, string>
      >((acc, desc) => {
        acc[`risk_level:${desc.risk_level}`] = desc.description;
        return acc;
      }, {});
      initialize(initialFormValues);
    };

    fetchData();
  }, [initialize, riskGroup]);

  const saveRiskDescriptions = async (formValues: Record<string, string>) => {
    try {
      const riskDescriptions = Object.entries(formValues).map(
        ([key, value]) => {
          const riskLevelMatch = key.match(/^risk_level:(\d+)$/);
          if (!riskLevelMatch) {
            throw new Error(`Unexpected key format: ${key}`);
          }

          return {
            risk_level: parseInt(riskLevelMatch[1], 10),
            risk_description: value
          };
        }
      );

      await api.saveFirmRiskDescriptions({
        risk_descriptions: riskDescriptions
      });
      Notify.success('Risk descriptions saved successfully.');
      setDirty(false);
      initialize(formValues);
    } catch (error) {
      Notify.error('Failed to save risk descriptions. Please try again.');
    }
  };

  const resetRiskDescriptions = async () => {
    try {
      const defaultResponse = await api.getRiskDescriptions();

      await api.deleteFirmRiskDescriptions();

      const initialFormValues: Record<string, string> =
        defaultResponse.data.reduce(
          (acc: Record<string, string>, desc: Description) => {
            acc[`risk_level:${desc.risk_level}`] = desc.description;
            return acc;
          },
          {}
        );

      initialize(initialFormValues);
      setResetModalVisible(false);
      Notify.success('Risk descriptions have been reset successfully.');
    } catch (error) {
      Notify.error('Failed to reset risk descriptions. Please try again.');
    }
  };

  const dispatch: Dispatch = useDispatch();

  const handleReplaceDescription = (desc: Description) => {
    if (!clickedDescription) {
      return;
    }
    const fieldName = `risk_level:${clickedDescription.risk_level}`;
    const newDescription = formValues?.[`risk_level:${desc.risk_level}`];
    dispatch(change('riskDescriptionsForm', fieldName, newDescription) as any);
    setCopyModalVisible(false);
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      <BoxWrapper
        title="Risk Descriptions"
        subTitle="Customise the risk profile descriptions used across Fintegrate for your firm. By default, these descriptions are provided by EV, but firm admin users can modify them to align with the firm’s approach to risk assessment. Note: Changes made here will apply across your firm."
        dropdown={true}
      >
        <form onSubmit={handleSubmit(saveRiskDescriptions)}>
          {customDescriptions.map((description, index) => {
            return (
              <BoxWrapper
                key={description.risk_level}
                title={`${description.risk_level}: ${description.risk_type}`}
                item
                dropdown
                headerChildren={
                  <span
                    style={{ display: 'flex' }}
                    ref={el => (iconContainerRefs.current[index] = el)}
                  >
                    <ContentCopy
                      fontSize="small"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        const ref = iconContainerRefs.current[index];
                        if (ref) {
                          setClickedIconRef({ current: ref });
                        }
                        setCopyModalVisible(!isCopyModalVisible);
                        setClickedDescription(description);
                      }}
                      style={{
                        transition: 'color 0.2s'
                      }}
                      onMouseEnter={e => (e.currentTarget.style.color = 'grey')}
                      onMouseLeave={e =>
                        (e.currentTarget.style.color = 'black')
                      }
                    />
                  </span>
                }
                alignHeaderChildrenLeft={true}
              >
                <Field
                  name={`risk_level:${description.risk_level}`}
                  component={Fields.Text}
                  className={classes.field}
                  multiline={true}
                  minRows={3}
                />
              </BoxWrapper>
            );
          })}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '1rem',
              position: 'sticky',
              bottom: 0,
              zIndex: 9,
              borderRadius: '25px',
              margin: '0 auto',
              background: '#ffffff',
              paddingBottom: '1rem'
            }}
          >
            <div>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={() => setResetModalVisible(true)}
                disabled={!isFirmAdmin}
                style={{ marginRight: '1rem', marginLeft: '1rem' }}
              >
                Reset
              </Button>
              Reset to default descriptions (provided by EV)
            </div>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              size="medium"
              disabled={!isFirmAdmin}
              style={{ marginRight: '1rem' }}
            >
              Save
            </Button>
          </div>
        </form>
      </BoxWrapper>
      <Prompt
        isOpen={isLeaveModalVisible}
        title={`You have unsaved changes, are you sure you want to leave?`}
        confirmAction={() => setConfirmLeave(true)}
        cancelAction={() => {
          setTargetLocation(null);
          setLeaveModalVisible(false);
        }}
        isProcessing={false}
      />
      <Prompt
        isOpen={isResetModalVisible}
        title={`Are you sure you want to reset risk descriptions to their default settings?`}
        confirmAction={() => resetRiskDescriptions()}
        cancelAction={() => {
          setResetModalVisible(false);
        }}
        isProcessing={false}
      />
      {isCopyModalVisible && clickedDescription && (
        <FloatingModal
          buttonRef={clickedIconRef}
          onClose={() => setCopyModalVisible(false)}
        >
          Copy description from:
          {customDescriptions
            .filter(desc => desc !== clickedDescription)
            .map(desc => (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                key={desc.risk_level}
                onClick={() => handleReplaceDescription(desc)}
                style={{ gap: '0.5rem', marginLeft: '0.5rem' }}
              >
                {desc.risk_level}
              </Button>
            ))}
        </FloatingModal>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  formValues: getFormValues('riskDescriptionsForm')(state),
  riskGroup: selectors.firmSelectors.riskGroup(state) || {}
});

const RiskDescriptionsForm = reduxForm<
  Record<string, string>,
  RiskDescriptionsProps
>({
  form: 'riskDescriptionsForm'
})(connect(mapStateToProps)(RiskDescriptionsBase));

const RiskDescriptions: React.FC<RiskDescriptionsProps> = props => {
  return <RiskDescriptionsForm {...props} />;
};

export default RiskDescriptions;
